// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// THE LEDGER — the landing view, and the whole argument of the site on one page.
//
// Two quantities, side by side, for every year the survey covers: what the
// farming earned (profit at full equity, teal) and what the land appreciated
// (capital appreciation, ochre). Then the running total of both, then the one
// question that decides whether the running total means anything — does the
// answer depend on where you start counting? — and then the same ledger region
// by region.
//
// Three decisions in here are deliberate and are the reason this file is long:
//
//   * THE PROVISIONAL YEAR IS NEVER SUMMED. 2024–25 is drawn, hatched, in the
//     year-by-year chart, and is excluded from every total, mean and share on
//     the page. ABARES' published headline sums (meta.headline.profitFeSum)
//     DO include it; this view recomputes from core.settledYears instead and
//     prints what the provisional year would have added, rather than quietly
//     folding an estimate that will be revised into a 35-year total.
//
//   * NOTHING BELOW ZERO IS DRAWN IN OCHRE OR TEAL. The cumulative land line
//     spends its first four years underwater, so it is split at the zero
//     crossing and the underwater run is drawn in crimson. Hue stays redundant
//     with position, which is what makes the charts readable without colour.
//
//   * THE NATIONAL SERIES CARRIES NO EVIDENCE TIER. ABARES publishes no
//     sampling error for the national aggregate in this extract, so the
//     national marks say so in their tooltips instead of borrowing a tier they
//     have not earned. The tier machinery does real work on the regional table
//     at the bottom, where the tiers exist.

import { loadCore, loadMeta } from '../data';
import { CRIMSON, FARMING, LAND, NEUTRAL, SCOPE_SHORT } from '../theme';
import {
  TIER_WORD, collapse, evidenceLines, isGap, isOutlineOnly,
  isRankable, isTokenSized, tierOpacity, tierPrefix, type Tier,
} from '../uncertainty';
import {
  axisBottom, axisLeft, brokenPath, chartSvg, el, extent, finite, hatchDefs,
  linearScale, markTip,
} from '../components/svg';
import { esc, fy, money, num, pct, signedPct, tipAttr } from '../format';
import { glossIcon, glossTerm } from '../components/glossary';
import { closeButton, openOverlay } from '../overlay';
import { openRegion } from '../components/regionDrawer';
import { currentRoute, navigate, setParams } from '../router';
import type { Core, NationalYear } from '../types';

/**
 * src/types.ts still describes the earlier `rel: Reliability` field. The JSON
 * on disk carries the five-tier evidence system instead — `tier` on the rate
 * cell, `apprTier` on the appreciation cell, and a region-level `tier` — so the
 * shapes this view actually reads are declared here rather than guessed at.
 */
interface LedgerYear {
  year: number;
  rateEx: number | null;
  rateInc: number | null;
  rateExRse: number | null;
  rateIncRse: number | null;
  sample: number | null;
  tier: Tier;
  apprTier: Tier;
  profitFe: number | null;
  appreciation: number | null;
}

interface LedgerRegion {
  name: string;
  code: number;
  zone: string;
  state: string | null;
  /** The region's OWN first surveyed year — three NT regions start in 1992. */
  firstYear: number;
  landShare: number | null;
  medianSample: number | null;
  minSample: number | null;
  weakYears: number;
  worstYearTier: Tier;
  tier: Tier;
  series: LedgerYear[];
}

const ENGINE_W = 1060;
const ENGINE_H = 380;
const CUM_W = 1000;
const CUM_H = 340;
const START_W = 820;
const START_H = 300;
const BAR_W = 170;
const BAR_H = 16;

export async function renderLedger(host: HTMLElement): Promise<void> {
  const [core, meta] = await Promise.all([loadCore(), loadMeta()]);
  const regions = core.regions as unknown as LedgerRegion[];

  const settled = new Set(core.settledYears);
  const lastSettled = core.settledYears[core.settledYears.length - 1];
  const provisional = core.provisionalYear;

  // Every start year that still leaves at least five settled years to sum.
  const startYears = core.settledYears.filter((y) => lastSettled - y + 1 >= 5);
  const firstStart = startYears[0];

  let fromYear = readFrom();

  function readFrom(): number {
    const raw = Number(currentRoute().params.get('from'));
    return startYears.includes(raw) ? raw : firstStart;
  }

  // meta.json carries survey.sample.{min,median,max}; the Meta interface in
  // src/types.ts predates it, so the field is read through a narrow local shape
  // rather than by widening a type this view does not own.
  const medianSample = (meta.survey as { sample?: { median?: number | null } }).sample?.median ?? null;

  host.innerHTML = shell(core, medianSample);

  const statsBox = host.querySelector<HTMLElement>('[data-slot="stats"]')!;
  const windowBox = host.querySelector<HTMLElement>('[data-slot="window"]')!;
  const engineBox = host.querySelector<HTMLElement>('[data-slot="engines"]')!;
  const cumBox = host.querySelector<HTMLElement>('[data-slot="cumulative"]')!;
  const cumReadout = host.querySelector<HTMLElement>('[data-slot="cum-readout"]')!;
  const startBox = host.querySelector<HTMLElement>('[data-slot="startyear"]')!;
  const counterBox = host.querySelector<HTMLElement>('[data-slot="counterweight"]')!;
  const tableBox = host.querySelector<HTMLElement>('[data-slot="regions"]')!;

  paint();
  buildRegionTable(tableBox, regions, settled);

  // One delegated listener per container, all of them on nodes INSIDE `host`,
  // so a navigation that replaces the host takes every listener with it.
  //
  // These two containers SURVIVE a repaint (only their innerHTML is replaced),
  // so their listeners are attached once here rather than inside paint() —
  // attaching in paint() would stack a fresh handler on every click and open
  // the same overlay two, then three, then four times.
  windowBox.addEventListener('click', (e) => {
    if ((e.target as Element).closest('[data-reset]')) setFrom(firstStart);
  });
  counterBox.addEventListener('click', (e) => {
    const t = (e.target as Element).closest('[data-negyear]');
    if (t) openYear(Number(t.getAttribute('data-negyear')));
  });
  host.addEventListener('click', (e) => {
    const t = (e.target as Element).closest('[data-go-view]');
    if (t) navigate(t.getAttribute('data-go-view') ?? '');
  });

  function setFrom(year: number): void {
    if (year === fromYear) return;
    fromYear = year;
    // replaceState, not a hash change: rescoping the page is not a navigation,
    // and it leaves the window shareable in the URL.
    setParams({ from: year === firstStart ? null : year });
    paint();
  }

  function paint(): void {
    const w = windowTotals(core.national, settled, fromYear);
    const r = rateCheck(core.national, settled, fromYear);
    drawStats(statsBox, w, r);
    drawWindowPill(windowBox, fromYear, lastSettled, firstStart, w.years);
    drawEngines(engineBox, core.national, provisional, fromYear, openYear);
    drawCumulative(cumBox, cumReadout, core.national, settled, fromYear, lastSettled, provisional, openYear);
    drawStartYears(startBox, core.national, settled, startYears, lastSettled, fromYear, setFrom);
    drawCounterweight(counterBox, core.national, regions, settled, fromYear);
  }

  function openYear(year: number): void {
    openYearOverlay(core, year);
  }
}

/* ─────────────────────────────── the page ─────────────────────────────────── */

function shell(core: Core, medianSample: number | null): string {
  return `
    <div class="view-head">
      <h1>The ledger</h1>
      <p class="lede">Every year since 1989–90, ${glossTerm('aagis', 'the ABARES survey')} has asked a few thousand
        ${glossTerm('broadacre', 'broadacre')} farms the same two questions: what did the farming earn, and what did the
        assets underneath it become worth? This page puts those two answers next to each other and adds them up.</p>
      <p>The surveyed farms earned money from farming in most years. They earned a great deal more from land
        appreciating. Nothing here is a claim that farming is a bad business — it is a claim about
        <em>where the return came from</em>, which is a different and more awkward thing.</p>
    </div>

    <section class="stack" aria-label="The headline ledger">
      <div data-slot="window"></div>
      <div class="stat-row" data-slot="stats"></div>
      <p class="note"><strong>Two methods, one answer.</strong> The share above is the land's part of
        ${glossTerm('landShare', 'the summed dollars')}. Averaging the two published
        ${glossTerm('rateOfReturn', 'rate-of-return')} series instead — one counting the farming only, one counting
        the land as well — gives a share a few points lower. Because the two methods differ, this site says
        <em>roughly three quarters</em> and never quotes a single decimal as if it settled the matter.</p>
    </section>

    <section class="card" style="margin-top: var(--space-xl)" aria-labelledby="engines-h">
      <h2 class="card-title" id="engines-h">Two engines, year by year</h2>
      <p class="card-sub">Each financial year gets a pair of columns: on the left what the farming earned
        (${glossTerm('profitAtFullEquity', 'profit at full equity')}), on the right what the land and other assets
        appreciated (${glossTerm('capitalAppreciation', 'capital appreciation')}). Both are dollars
        ${glossTerm('perFarm', 'per surveyed farm')}, ${glossTerm('nominal', 'unadjusted for inflation')}. Columns
        below the zero line mean the farms went backwards on that measure that year.
        Click any year for its full ledger.</p>
      <div data-slot="engines"></div>
    </section>

    <section class="card" style="margin-top: var(--space-xl)" aria-labelledby="cum-h">
      <h2 class="card-title" id="cum-h">The running total</h2>
      <p class="card-sub">The same two quantities, added up as you go. Each line answers "how many dollars per
        surveyed farm has this engine delivered since the start of the window?" The gap between the lines at the
        right-hand edge is the finding.</p>
      <div data-slot="cumulative"></div>
      <div data-slot="cum-readout" class="stack" style="margin-top: var(--space-md)"></div>
    </section>

    <div class="grid-2" style="margin-top: var(--space-xl)">
      <section class="card" aria-labelledby="start-h">
        <h2 class="card-title" id="start-h">Does the answer depend on where you start?</h2>
        <p class="card-sub">A long-run total can be manufactured by choosing a flattering first year. So: every
          possible start year, and the land's share of the total return from that year to ${esc(fy(core.settledYears[core.settledYears.length - 1]))}.
          Click a point to move the whole page's window to that start year.</p>
        <div data-slot="startyear"></div>
      </section>
      <section class="card" aria-labelledby="counter-h">
        <h2 class="card-title" id="counter-h">The land does not only go up</h2>
        <div data-slot="counterweight" class="stack" style="margin-top: var(--space-sm)"></div>
      </section>
    </div>

    <section class="card" style="margin-top: var(--space-xl)" aria-labelledby="regions-h">
      <h2 class="card-title" id="regions-h">The same ledger, region by region ${glossIcon('rse')}</h2>
      <p class="card-sub">What the farming alone returned on the capital tied up in it, averaged across the settled
        years, in each of the 32 survey regions. This is a sample survey — the typical regional estimate rests on
        about ${medianSample == null ? 'forty' : esc(num(medianSample))} farms — so each region is drawn at the weight
        of its evidence, and regions that cannot support a ranking are not ranked. Click a region for its own ledger.</p>
      <div data-slot="regions"></div>
    </section>

    <section class="card" style="margin-top: var(--space-xl)" aria-labelledby="next-h">
      <h2 class="card-title" id="next-h">Where this argument goes next</h2>
      <p class="card-sub">This page establishes the gap. The rest of the site takes it apart.</p>
      <div class="row" style="flex-wrap: wrap; margin-top: var(--space-md)">
        <button type="button" class="btn btn-sm" data-go-view="engines">The two engines, in detail</button>
        <button type="button" class="btn btn-sm" data-go-view="ground">Where farming actually pays</button>
        <button type="button" class="btn btn-sm" data-go-view="mechanism">Why the gap opened</button>
      </div>
    </section>

    <p class="xsmall muted" style="margin-top: var(--space-xl)">${esc(SCOPE_SHORT)}</p>
  `;
}

/* ────────────────────────────── the arithmetic ────────────────────────────── */

interface Totals { farming: number; land: number; years: number; share: number | null; negLand: number }

/**
 * Sum the window. `settled` does the work of excluding the provisional year:
 * an estimate ABARES will revise has no business inside a 35-year total.
 * Missing values are skipped, never treated as zero — a zero would be a claim.
 */
function windowTotals(nat: NationalYear[], settled: Set<number>, from: number): Totals {
  let farming = 0; let land = 0; let years = 0; let negLand = 0;
  for (const n of nat) {
    if (!settled.has(n.year) || n.year < from) continue;
    if (n.profitFe == null || n.appreciation == null) continue;
    farming += n.profitFe;
    land += n.appreciation;
    if (n.appreciation < 0) negLand++;
    years++;
  }
  const total = farming + land;
  // A share is only meaningful when both parts pull the same way. If the
  // farming total were negative the "share" would exceed 100% and mean nothing.
  const share = total > 0 && farming > 0 && land > 0 ? (land / total) * 100 : null;
  return { farming, land, years, share, negLand };
}

/** The independent cross-check: the gap between the two published rate series. */
function rateCheck(nat: NationalYear[], settled: Set<number>, from: number): { ex: number | null; inc: number | null; share: number | null } {
  const ex: number[] = []; const inc: number[] = [];
  for (const n of nat) {
    if (!settled.has(n.year) || n.year < from) continue;
    if (n.rateEx != null) ex.push(n.rateEx);
    if (n.rateInc != null) inc.push(n.rateInc);
  }
  const mEx = ex.length ? ex.reduce((a, b) => a + b, 0) / ex.length : null;
  const mInc = inc.length ? inc.reduce((a, b) => a + b, 0) / inc.length : null;
  const share = mEx != null && mInc != null && mInc > 0 && mEx > 0 ? ((mInc - mEx) / mInc) * 100 : null;
  return { ex: mEx, inc: mInc, share };
}

/* ─────────────────────────────── the stats ────────────────────────────────── */

function drawStats(box: HTMLElement, w: Totals, r: { ex: number | null; inc: number | null; share: number | null }): void {
  const shareWord = w.share == null ? 'not meaningful'
    : w.share >= 70 && w.share <= 80 ? 'roughly three quarters'
      : w.share >= 60 && w.share < 70 ? 'roughly two thirds'
        : w.share >= 80 ? 'more than four fifths' : 'under three fifths';
  box.innerHTML = `
    <div class="stat">
      <div class="stat-value farming mono">${esc(money(w.farming))}</div>
      <div class="stat-label">What the farming earned</div>
      <div class="stat-note">Summed profit at full equity over ${esc(String(w.years))} settled years, per surveyed farm.</div>
    </div>
    <div class="stat">
      <div class="stat-value land mono">${esc(money(w.land))}</div>
      <div class="stat-label">What the land appreciated</div>
      <div class="stat-note">Summed capital appreciation over the same years and the same farms.</div>
    </div>
    <div class="stat">
      <div class="stat-value">${esc(shareWord)}</div>
      <div class="stat-label">of the total return came from the land</div>
      <div class="stat-note">${esc(pct(w.share))} by summed dollars · ${esc(pct(r.share))} by the rate method.</div>
    </div>
    <div class="stat">
      <div class="stat-value mono">${esc(signedPct(r.ex))}</div>
      <div class="stat-label">a year, counting the farming only</div>
      <div class="stat-note">Against ${esc(signedPct(r.inc))} a year once the land is counted — the mean of the
        published annual rates, provisional year excluded.</div>
    </div>`;
}

function drawWindowPill(box: HTMLElement, from: number, last: number, firstStart: number, years: number): void {
  const custom = from !== firstStart;
  box.innerHTML = `<div class="row" style="flex-wrap: wrap">
      <span class="pill">Window: ${esc(fy(from))} to ${esc(fy(last))} · ${esc(String(years))} settled years</span>
      ${custom ? '<button type="button" class="pill pill-clear" data-reset>Reset to the full run ✕</button>' : ''}
      <span class="xsmall muted truncate">The provisional year is drawn but never added in.</span>
    </div>`;
}

/* ──────────────────────── chart 1 · two engines a year ────────────────────── */

function drawEngines(
  box: HTMLElement,
  nat: NationalYear[],
  provisional: number | null,
  from: number,
  onYear: (year: number) => void,
): void {
  box.innerHTML = '';
  const M = { t: 16, r: 16, b: 54, l: 76 };
  const svg = chartSvg(ENGINE_W, ENGINE_H, 'What the farming earned and what the land appreciated, per surveyed farm, for every year of the survey');
  // Wider than a phone by nature, so it scrolls inside its own box rather than
  // giving the page a sideways scroll.
  svg.style.minWidth = '780px';
  svg.append(
    hatchDefs('ledger-hatch-farming', FARMING),
    hatchDefs('ledger-hatch-land', LAND),
    hatchDefs('ledger-hatch-neg', CRIMSON),
  );

  const [lo, hi] = extent([...nat.map((n) => n.profitFe), ...nat.map((n) => n.appreciation), 0]);
  const y = linearScale([Math.min(0, lo), Math.max(0, hi)], [ENGINE_H - M.b, M.t]);
  const x = linearScale([0, nat.length - 1], [M.l + (ENGINE_W - M.l - M.r) / nat.length / 2, ENGINE_W - M.r - (ENGINE_W - M.l - M.r) / nat.length / 2]);
  const band = (ENGINE_W - M.l - M.r) / nat.length;
  const bw = Math.max(4, band * 0.38);

  axisLeft(svg, y, M.l, { format: (v) => money(v), gridTo: ENGINE_W - M.r, title: 'Dollars per surveyed farm' });
  axisBottom(svg, x, ENGINE_H - M.b, {
    ticks: nat.map((_, i) => i).filter((i) => i % 5 === 0 || i === nat.length - 1),
    format: (i) => fy(nat[i]?.year),
    title: 'Financial year',
    x0: M.l,
    x1: ENGINE_W - M.r,
  });

  for (let i = 0; i < nat.length; i++) {
    const n = nat[i];
    const cx = finite(x(i));
    const outside = n.year < from;
    const prov = provisional != null && n.year === provisional;

    const hit = el('rect', {
      x: finite(cx - band / 2), y: M.t, width: finite(band), height: finite(ENGINE_H - M.b - M.t),
      fill: 'transparent', class: 'mark', tabindex: 0, role: 'button', 'data-year': n.year,
      style: 'cursor: pointer',
    });
    markTip(hit, [
      `${fy(n.year)} — click for the full ledger`,
      `Farming: ${money(n.profitFe, { long: true })}`,
      `Land: ${money(n.appreciation, { long: true })}`,
      prov && 'Provisional — excluded from every total on this page.',
      outside && 'Outside the current window.',
    ]);
    svg.append(hit);

    drawEngineBar(svg, n, 'profitFe', FARMING, cx - bw / 2 - 1, bw, y, prov, outside);
    drawEngineBar(svg, n, 'appreciation', LAND, cx + bw / 2 + 1, bw, y, prov, outside);
  }

  // One delegated pair of listeners for 36 hit areas, on a node inside `host`.
  svg.addEventListener('click', (e) => {
    const t = (e.target as Element).closest('[data-year]');
    if (t) onYear(Number(t.getAttribute('data-year')));
  });
  svg.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const t = (e.target as Element).closest('[data-year]');
    if (!t) return;
    e.preventDefault();
    onYear(Number(t.getAttribute('data-year')));
  });

  const frame = document.createElement('div');
  frame.className = 'chart-frame';
  const scroll = document.createElement('div');
  scroll.className = 'chart-scroll';
  scroll.append(svg);
  frame.append(scroll);
  box.append(frame);

  const legend = document.createElement('div');
  legend.className = 'legend';
  legend.innerHTML = `
    ${swatch(FARMING, 'What the farming earned — the left column of each pair')}
    ${swatch(LAND, 'What the land appreciated — the right column')}
    ${swatch(CRIMSON, 'Below the zero line — either quantity, whichever went backwards')}
    <span class="legend-item"><span class="legend-swatch" style="background: repeating-linear-gradient(45deg, ${LAND} 0 2px, transparent 2px 5px); border: 1px solid ${LAND}"></span>
      ${esc(fy(provisional))} is provisional and is drawn hatched</span>`;
  box.append(legend);
}

function drawEngineBar(
  svg: SVGSVGElement,
  n: NationalYear,
  key: 'profitFe' | 'appreciation',
  colour: string,
  cx: number,
  bw: number,
  y: ReturnType<typeof linearScale>,
  prov: boolean,
  outside: boolean,
): void {
  const v = n[key];
  const label = key === 'profitFe' ? 'What the farming earned' : 'What the land appreciated';
  if (v == null || !Number.isFinite(v)) {
    // No estimate: leave the slot EMPTY and mark the gap. Never a zero column —
    // a zero column is a claim that the quantity was zero.
    const gap = el('rect', { x: finite(cx - bw / 2), y: finite(y(0)) - 1, width: finite(bw), height: 2, fill: NEUTRAL, opacity: 0.5, class: 'mark' });
    markTip(gap, [`${label}, ${fy(n.year)}: no estimate published`, ...evidenceLines(null, 5)]);
    svg.append(gap);
    return;
  }
  const y0 = finite(y(0));
  const y1 = finite(y(v));
  const neg = v < 0;
  const fill = neg ? CRIMSON : colour;
  const pattern = neg ? 'ledger-hatch-neg' : colour === FARMING ? 'ledger-hatch-farming' : 'ledger-hatch-land';
  const rect = el('rect', {
    x: finite(cx - bw / 2),
    y: finite(Math.min(y0, y1)),
    width: finite(bw),
    height: finite(Math.max(1, Math.abs(y1 - y0))),
    // The provisional year is hatched wherever it appears, so it can never be
    // mistaken for a settled column at a glance.
    fill: prov ? `url(#${pattern})` : fill,
    stroke: prov ? fill : 'none',
    'stroke-width': prov ? 1 : 0,
    class: `mark${outside ? ' dim' : ''}`,
  });
  markTip(rect, [
    `${label}, ${fy(n.year)}: ${money(v, { long: true })}`,
    ...evidenceLines(null, null),
    'National aggregate — ABARES publishes no sampling error for it here, so no evidence tier is claimed.',
    prov && 'Provisional: ABARES will revise it, and it is excluded from every total on this page.',
    outside && 'Outside the current window — not counted in the totals above.',
  ]);
  svg.append(rect);
}

/* ───────────────────────── chart 2 · the running total ────────────────────── */

interface SignedPoint { x: number; v: number | null }
interface SignedRun { neg: boolean; pts: Array<{ x: number; v: number }> }

/**
 * Split a series into runs of one sign, inserting the exact zero crossing.
 *
 * This exists so the ochre line can never appear below the zero line and the
 * crimson can never appear above it: hue stays redundant with position, and a
 * reader who cannot separate the two hues still reads every chart correctly.
 * A null breaks the run outright — nothing is bridged or interpolated.
 */
function signedRuns(points: SignedPoint[]): SignedRun[] {
  const runs: SignedRun[] = [];
  let cur: SignedRun | null = null;
  const push = (neg: boolean, p: { x: number; v: number }): void => {
    let c = cur;
    if (!c || c.neg !== neg) { c = { neg, pts: [] }; runs.push(c); cur = c; }
    c.pts.push(p);
  };
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    if (p.v == null || !Number.isFinite(p.v)) { cur = null; continue; }
    push(p.v < 0, { x: p.x, v: p.v });
    const q = points[i + 1];
    if (!q || q.v == null || !Number.isFinite(q.v)) continue;
    if ((p.v < 0) !== (q.v < 0)) {
      const t = p.v / (p.v - q.v);
      const xc = p.x + (q.x - p.x) * t;
      push(p.v < 0, { x: xc, v: 0 });
      push(q.v < 0, { x: xc, v: 0 });
    }
  }
  return runs;
}

function drawCumulative(
  box: HTMLElement,
  readout: HTMLElement,
  nat: NationalYear[],
  settled: Set<number>,
  from: number,
  last: number,
  provisional: number | null,
  onYear: (year: number) => void,
): void {
  box.innerHTML = '';
  const M = { t: 18, r: 18, b: 52, l: 80 };
  const svg = chartSvg(CUM_W, CUM_H, 'The running total of what the farming earned and what the land appreciated, per surveyed farm');
  svg.style.minWidth = '640px';

  const rows = nat.filter((n) => settled.has(n.year) && n.year >= from);
  let cf = 0; let cl = 0;
  const series = rows.map((n) => {
    if (n.profitFe != null) cf += n.profitFe;
    if (n.appreciation != null) cl += n.appreciation;
    return {
      year: n.year,
      // A null contribution breaks the cumulative line rather than carrying the
      // previous total forward, which would draw a flat year that never happened.
      farming: n.profitFe == null ? null : cf,
      land: n.appreciation == null ? null : cl,
      addFarming: n.profitFe,
      addLand: n.appreciation,
    };
  });

  const [lo, hi] = extent([...series.map((s) => s.farming), ...series.map((s) => s.land), 0]);
  const y = linearScale([Math.min(0, lo), Math.max(0, hi)], [CUM_H - M.b, M.t]);
  const x = linearScale([from, last], [M.l, CUM_W - M.r]);

  axisLeft(svg, y, M.l, { format: (v) => money(v), gridTo: CUM_W - M.r, title: 'Cumulative dollars per farm' });
  axisBottom(svg, x, CUM_H - M.b, {
    ticks: series.map((s) => s.year).filter((yr, i) => i === 0 || yr % 5 === 0 || yr === last),
    format: (v) => fy(v),
    title: 'Financial year',
  });

  for (const [key, colour] of [['farming', FARMING], ['land', LAND]] as const) {
    const runs = signedRuns(series.map((s) => ({ x: s.year, v: s[key] })));
    for (const run of runs) {
      const ds = brokenPath(run.pts.map((p) => ({ x: finite(x(p.x)), y: finite(y(p.v)) })));
      for (const d of ds) {
        svg.append(el('path', {
          d,
          fill: 'none',
          stroke: run.neg ? CRIMSON : colour,
          'stroke-width': 2.4,
          'stroke-linejoin': 'round',
          'stroke-linecap': 'round',
        }));
      }
    }
  }

  for (const s of series) {
    for (const [key, colour, label] of [
      ['farming', FARMING, 'Farming, running total'],
      ['land', LAND, 'Land, running total'],
    ] as const) {
      const v = s[key];
      if (v == null || !Number.isFinite(v)) continue;
      const dot = el('circle', {
        cx: finite(x(s.year)), cy: finite(y(v)), r: 3,
        fill: v < 0 ? CRIMSON : colour, class: 'mark',
      });
      markTip(dot, [
        `${label} to ${fy(s.year)}: ${money(v, { long: true })}`,
        `That year alone added ${money(key === 'farming' ? s.addFarming : s.addLand, { long: true })}.`,
        ...evidenceLines(null, null),
        'National aggregate — no sampling error is published for it here.',
      ]);
      svg.append(dot);
    }
    const hit = el('rect', {
      x: finite(x(s.year) - 6), y: M.t, width: 12, height: finite(CUM_H - M.b - M.t),
      fill: 'transparent', class: 'mark', tabindex: 0, role: 'button', 'data-year': s.year,
      style: 'cursor: pointer',
    });
    markTip(hit, [`${fy(s.year)} — click for the full ledger of that year`]);
    svg.append(hit);
  }

  svg.addEventListener('click', (e) => {
    const t = (e.target as Element).closest('[data-year]');
    if (t) onYear(Number(t.getAttribute('data-year')));
  });
  svg.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const t = (e.target as Element).closest('[data-year]');
    if (!t) return;
    e.preventDefault();
    onYear(Number(t.getAttribute('data-year')));
  });

  const frame = document.createElement('div');
  frame.className = 'chart-frame';
  const scroll = document.createElement('div');
  scroll.className = 'chart-scroll';
  scroll.append(svg);
  frame.append(scroll);
  box.append(frame);

  const legend = document.createElement('div');
  legend.className = 'legend';
  legend.innerHTML = `
    ${swatch(FARMING, 'Running total of what the farming earned')}
    ${swatch(LAND, 'Running total of what the land appreciated')}
    ${swatch(CRIMSON, 'The same running total while it is still below zero')}`;
  box.append(legend);

  const provRow = provisional == null ? null : nat.find((n) => n.year === provisional);
  const w = windowTotals(nat, settled, from);
  readout.innerHTML = `
    <p class="small">Over the ${esc(String(w.years))} settled years from ${esc(fy(from))}, each surveyed broadacre farm
      earned <strong class="mono" style="color: var(--farming)">${esc(money(w.farming, { long: true }))}</strong> from
      farming and <strong class="mono" style="color: var(--land)">${esc(money(w.land, { long: true }))}</strong> from
      its assets appreciating.</p>
    ${provRow ? `<p class="provisional-note">${esc(fy(provRow.year))} is provisional and is not added in. It would add
      ${esc(money(provRow.profitFe, { long: true }))} of farming and ${esc(money(provRow.appreciation, { long: true }))}
      of appreciation — enough to move the share, which is why it waits until ABARES settles it.</p>` : ''}`;
}

/* ─────────────────── chart 3 · does the start year decide it? ─────────────── */

function drawStartYears(
  box: HTMLElement,
  nat: NationalYear[],
  settled: Set<number>,
  startYears: number[],
  last: number,
  selected: number,
  onPick: (year: number) => void,
): void {
  box.innerHTML = '';
  const M = { t: 16, r: 18, b: 52, l: 52 };
  const svg = chartSvg(START_W, START_H, "The land's share of the total return, for every possible start year");
  svg.style.minWidth = '520px';

  const pts = startYears.map((yr) => ({ year: yr, ...windowTotals(nat, settled, yr) }));
  const x = linearScale([startYears[0], startYears[startYears.length - 1]], [M.l, START_W - M.r]);
  // Anchored at zero and topped at 100: a share axis cropped to its own range
  // would turn a flat band into a dramatic mountain.
  const y = linearScale([0, 100], [START_H - M.b, M.t]);

  axisLeft(svg, y, M.l, { format: (v) => `${v}%`, ticks: [0, 25, 50, 75, 100], gridTo: START_W - M.r, title: "Land's share" });
  axisBottom(svg, x, START_H - M.b, {
    ticks: startYears.filter((yr, i) => i === 0 || yr % 5 === 0 || i === startYears.length - 1),
    format: (v) => fy(v),
    title: 'Counting from',
  });

  for (const d of brokenPath(pts.map((p) => ({ x: finite(x(p.year)), y: p.share == null ? null : finite(y(p.share)) })))) {
    svg.append(el('path', { d, fill: 'none', stroke: LAND, 'stroke-width': 2.2, 'stroke-linejoin': 'round' }));
  }

  for (const p of pts) {
    if (p.share == null) continue;
    const sel = p.year === selected;
    const dot = el('circle', {
      cx: finite(x(p.year)), cy: finite(y(p.share)), r: sel ? 6 : 4.5,
      fill: sel ? LAND : '#ffffff', stroke: LAND, 'stroke-width': 2,
      class: `mark${sel ? ' sel' : ''}`, tabindex: 0, role: 'button', 'data-start': p.year,
      style: 'cursor: pointer',
    });
    markTip(dot, [
      `Counting from ${fy(p.year)}: the land is ${pct(p.share)} of the total return`,
      `${money(p.farming, { long: true })} from farming, ${money(p.land, { long: true })} from the land, over ${p.years} settled years.`,
      'Click to move the whole page to this start year.',
    ]);
    svg.append(dot);
  }

  svg.addEventListener('click', (e) => {
    const t = (e.target as Element).closest('[data-start]');
    if (t) onPick(Number(t.getAttribute('data-start')));
  });
  svg.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const t = (e.target as Element).closest('[data-start]');
    if (!t) return;
    e.preventDefault();
    onPick(Number(t.getAttribute('data-start')));
  });

  const frame = document.createElement('div');
  frame.className = 'chart-frame';
  const scroll = document.createElement('div');
  scroll.className = 'chart-scroll';
  scroll.append(svg);
  frame.append(scroll);
  box.append(frame);

  const shares = pts.map((p) => p.share).filter((s): s is number => s != null);
  const legend = document.createElement('div');
  legend.className = 'legend';
  legend.innerHTML = `${swatch(LAND, `The land's share of the total return from that year to ${fy(last)}`)}
    <span class="legend-item">Filled point = the window this page is currently using</span>`;
  box.append(legend);

  const note = document.createElement('p');
  note.className = 'note caveat small';
  note.innerHTML = shares.length
    ? `<strong>It does not.</strong> Every start year from ${esc(fy(startYears[0]))} onwards puts the land between
       ${esc(pct(Math.min(...shares)))} and ${esc(pct(Math.max(...shares)))} of the total return. Starting after the
       land-price stall of the early 2010s gives a <em>higher</em> share, not a lower one — the finding survives every
       start year available, which is the only reason it is worth stating.`
    : 'No start year in this window has both a positive farming total and a positive land total.';
  box.append(note);
}

/* ───────────────────────── the counterweight panel ────────────────────────── */

function drawCounterweight(
  box: HTMLElement,
  nat: NationalYear[],
  regions: LedgerRegion[],
  settled: Set<number>,
  from: number,
): void {
  const negYears = nat
    .filter((n) => settled.has(n.year) && n.appreciation != null && n.appreciation < 0)
    .map((n) => n.year);
  const inWindow = negYears.filter((y) => y >= from);

  let negCells = 0; let totalCells = 0;
  for (const r of regions) {
    for (const yr of r.series) {
      if (yr.appreciation == null) continue;
      totalCells++;
      if (yr.appreciation < 0) negCells++;
    }
  }

  box.innerHTML = `
    <p class="small">Capital appreciation is a paper gain, and it goes backwards. Nationally it was negative in
      <strong>${esc(String(negYears.length))} of the ${esc(String(settled.size))} settled years</strong>${inWindow.length === negYears.length ? ''
    : `, of which ${esc(String(inWindow.length))} fall inside the current window`}, and it did so in two clusters rather
      than at random: the early-1990s slump and the stall of the early 2010s.</p>
    <p class="small">Region by region it is commoner still: appreciation was negative in
      <strong>${esc(num(negCells))} of the ${esc(num(totalCells))} region-years</strong> the survey covers. A farm that
      counted on the land to carry it had roughly a one-in-four chance, in any given region-year, of the land taking
      something away instead.</p>
    <div class="row" style="flex-wrap: wrap; gap: var(--space-sm)">
      ${negYears.map((y) => `<button type="button" class="pill pill-clear" data-negyear="${y}"
        data-tip="${tipAttr(`${fy(y)}: the land went backwards`, 'Click for the full ledger of that year')}">${esc(fy(y))}</button>`).join('')}
    </div>
    <p class="xsmall muted">Each year above opens its ledger. ${glossTerm('capitalAppreciation', 'What capital appreciation is')}</p>`;
}

/* ─────────────────────── the region table (tier-encoded) ──────────────────── */

interface RegionRow {
  region: LedgerRegion;
  mean: number | null;
  meanInc: number | null;
  years: number;
  farming: number;
  land: number;
}

function regionRow(r: LedgerRegion, settled: Set<number>): RegionRow {
  // Each region is averaged over ITS OWN years. Three NT regions begin in 1992,
  // and averaging them across a 1990 denominator would invent two years of data.
  const ex: number[] = []; const inc: number[] = [];
  let farming = 0; let land = 0;
  for (const yr of r.series) {
    if (!settled.has(yr.year)) continue;
    if (yr.rateEx != null) ex.push(yr.rateEx);
    if (yr.rateInc != null) inc.push(yr.rateInc);
    if (yr.profitFe != null) farming += yr.profitFe;
    if (yr.appreciation != null) land += yr.appreciation;
  }
  return {
    region: r,
    mean: ex.length ? ex.reduce((a, b) => a + b, 0) / ex.length : null,
    meanInc: inc.length ? inc.reduce((a, b) => a + b, 0) / inc.length : null,
    years: ex.length,
    farming,
    land,
  };
}

function buildRegionTable(box: HTMLElement, regions: LedgerRegion[], settled: Set<number>): void {
  const rows = regions.map((r) => regionRow(r, settled));

  // A ranking takes tier 1 and 2 only, and the count of everything it left out
  // is printed on the page rather than buried in a caveat.
  const ranked = rows.filter((d) => isRankable(d.region.tier) && d.mean != null)
    .sort((a, b) => (b.mean ?? 0) - (a.mean ?? 0));
  const unranked = rows.filter((d) => !isRankable(d.region.tier) || d.mean == null)
    .sort((a, b) => (b.mean ?? -Infinity) - (a.mean ?? -Infinity));

  // The bar scale ignores tier-4 regions: their magnitude is not encoded
  // anywhere, so it must not be allowed to set everyone else's scale either.
  const scaleVals = rows.filter((d) => !isTokenSized(d.region.tier) && !isGap(d.region.tier)).map((d) => d.mean);
  const [lo, hi] = extent([...scaleVals, 0]);
  const x = linearScale([Math.min(0, lo), Math.max(0, hi)], [2, BAR_W - 2]);

  const wrap = document.createElement('div');
  wrap.className = 'table-scroll';
  const table = document.createElement('table');
  table.innerHTML = `
    <thead><tr>
      <th scope="col">#</th>
      <th scope="col">Region</th>
      <th scope="col" style="min-width: ${BAR_W + 16}px">Return from farming alone, a year</th>
      <th scope="col" class="num">Mean</th>
      <th scope="col" class="num">With the land</th>
      <th scope="col">Evidence</th>
    </tr></thead>`;
  const tbody = document.createElement('tbody');

  const addRow = (d: RegionRow, rank: number | null): void => {
    const r = d.region;
    const tr = document.createElement('tr');
    tr.className = 'clickable';
    // tabindex + Enter/Space rather than role="button": a <tr role="button">
    // stops being a row, and its cells become invalid children of it — the row
    // keeps its table semantics and gains a keyboard activation instead.
    tr.tabIndex = 0;
    tr.setAttribute('data-region', r.name);

    const readout = isTokenSized(r.tier) ? 'no basis'
      : isGap(r.tier) || d.mean == null ? '—'
        : `${tierPrefix(r.tier)}${signedPct(d.mean)}`;
    const readoutInc = isTokenSized(r.tier) ? 'no basis'
      : isGap(r.tier) || d.meanInc == null ? '—'
        : `${tierPrefix(r.tier)}${signedPct(d.meanInc)}`;

    tr.innerHTML = `
      <td class="num muted">${rank == null ? '—' : rank}</td>
      <td><span class="truncate" style="display: block; max-width: 26ch">${esc(r.name)}</span>
        <span class="xsmall muted">${esc(r.zone)}${r.firstYear !== 1990 ? ` · from ${esc(fy(r.firstYear))}` : ''}</span>
        <span class="visually-hidden">Press Enter for this region's own ledger.</span></td>
      <td data-bar></td>
      <td class="num ${d.mean != null && d.mean < 0 && !isTokenSized(r.tier) ? 'neg' : ''}">${esc(readout)}</td>
      <td class="num muted">${esc(readoutInc)}</td>
      <td class="xsmall muted">${esc(TIER_WORD[r.tier])} · ${esc(String(r.medianSample ?? '—'))} farms typical</td>`;

    const cell = tr.querySelector('[data-bar]')!;
    cell.append(regionBar(d, x));
    tbody.append(tr);
  };

  ranked.forEach((d, i) => addRow(d, i + 1));

  if (unranked.length) {
    const sep = document.createElement('tr');
    sep.innerHTML = `<td colspan="6" class="small" style="background: var(--bg-elevated)">
      <strong>${esc(String(unranked.length))} of ${esc(String(rows.length))} regions carry too much survey error to rank.</strong>
      They are drawn below in outline — or, where the survey cannot support a figure at all, as a bare token — and no
      ordering, mean or superlative anywhere on this site includes them.</td>`;
    tbody.append(sep);
    unranked.forEach((d) => addRow(d, null));
  }

  table.append(tbody);
  wrap.append(table);
  box.innerHTML = '';
  box.append(wrap);

  const legend = document.createElement('div');
  legend.className = 'legend';
  legend.innerHTML = `
    ${swatch(FARMING, 'Farming returned more than nothing on the capital')}
    ${swatch(CRIMSON, 'Farming returned less than nothing')}
    <span class="legend-item"><span class="legend-swatch" style="background: transparent; border: 1.5px solid ${collapse(FARMING, 3)}"></span>
      Outline only = weak evidence: read the direction, not the number</span>
    <span class="legend-item"><span class="legend-swatch" style="background: ${NEUTRAL}; width: 10px; height: 10px"></span>
      Fixed token = no basis: the survey cannot support a figure, so none is drawn</span>`;
  box.append(legend);

  const note = document.createElement('p');
  note.className = 'note caveat small';
  note.innerHTML = `Each figure is the mean of that region's published annual rates over the settled years, using the
    region's own first surveyed year. The provisional year is not in it. Where a region's farming return is negative
    its "land share" is arithmetically above 100% and is not reported — a share of a total that one part drags below
    zero is not a share of anything.`;
  box.append(note);

  // The SHARED region dossier, not a bespoke one: a region that shows different
  // numbers depending on which view you clicked it from is worse than no
  // drill-down at all.
  tbody.addEventListener('click', (e) => {
    const tr = (e.target as Element).closest('[data-region]');
    if (tr) openRegion(tr.getAttribute('data-region') ?? '');
  });
  tbody.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const tr = (e.target as Element).closest('[data-region]');
    if (!tr) return;
    e.preventDefault();
    openRegion(tr.getAttribute('data-region') ?? '');
  });
}

function regionBar(d: RegionRow, x: ReturnType<typeof linearScale>): SVGSVGElement {
  const r = d.region;
  const svg = chartSvg(BAR_W, BAR_H, `Mean return from farming alone in ${r.name}`);
  svg.style.width = `${BAR_W}px`;
  const x0 = finite(x(0));
  svg.append(el('line', { class: 'zero-line', x1: x0, y1: 0, x2: x0, y2: BAR_H }));

  const tipLines = (value: string): string[] => [
    `${r.name}: ${value}`,
    ...evidenceLines(r.medianSample, r.tier),
    `${r.weakYears} of its ${d.years} settled years are weak or worse.`,
    `Summed over those years: ${money(d.farming)} from farming, ${money(d.land)} from the land.`,
    // The NT regions enter the survey in 1992. Averaging or indexing them from
    // an assumed 1990 would invent two years they were never surveyed for.
    r.firstYear !== 1990 ? `First surveyed in ${fy(r.firstYear)}, so it is averaged over its own years only.` : false,
  ].filter((s): s is string => typeof s === 'string');

  // Tier 5 breaks the mark outright — nothing is drawn where nothing is known.
  if (isGap(r.tier) || d.mean == null) {
    const t = el('text', { x: x0 + 6, y: BAR_H - 4, class: 'axis-text' });
    t.textContent = 'no estimate';
    markTip(t, tipLines('no estimate published'));
    svg.append(t);
    return svg;
  }

  // Tier 4 never encodes magnitude: a fixed token, at zero, in the neutral —
  // it says "the survey cannot support a figure", not "the figure is small".
  if (isTokenSized(r.tier)) {
    const token = el('rect', { x: x0 - 5, y: BAR_H / 2 - 5, width: 10, height: 10, fill: NEUTRAL, class: 'mark' });
    markTip(token, tipLines('no basis for a figure'));
    svg.append(token);
    return svg;
  }

  const xv = finite(x(d.mean));
  const neg = d.mean < 0;
  const colour = collapse(neg ? CRIMSON : FARMING, r.tier);
  const rect = el('rect', {
    x: finite(Math.min(x0, xv)),
    y: 3,
    width: finite(Math.max(1.5, Math.abs(xv - x0))),
    height: BAR_H - 6,
    // Tier 3 shows position and sign but not magnitude-as-mass: outline only.
    fill: isOutlineOnly(r.tier) ? 'none' : colour,
    stroke: isOutlineOnly(r.tier) ? colour : 'none',
    'stroke-width': isOutlineOnly(r.tier) ? 1.5 : 0,
    opacity: tierOpacity(r.tier),
    class: 'mark',
  });
  markTip(rect, tipLines(`${tierPrefix(r.tier)}${signedPct(d.mean)} a year from farming alone`));
  svg.append(rect);
  return svg;
}

/* ────────────────────────────── the overlays ──────────────────────────────── */

/**
 * One year's ledger, as three sums that ABARES' own numbers actually satisfy.
 *
 * The fourth sum a reader expects — profit at full equity = business profit +
 * interest + land rent — does NOT hold in this data, so it is named and refused
 * rather than drawn as a tidy fourth line.
 */
function openYearOverlay(core: Core, year: number): void {
  openOverlay({
    panelClass: 'modal',
    label: `The ledger for ${fy(year)}`,
    lockScroll: true,
    build(panel, close) {
      let shown = year;
      const head = document.createElement('div');
      head.className = 'overlay-head';
      const h = document.createElement('h2');
      head.append(h, closeButton(close, 'Close this year'));
      const body = document.createElement('div');
      body.className = 'stack';
      panel.append(head, body);

      const fill = (yr: number): void => {
        shown = yr;
        const n = core.national.find((x) => x.year === yr);
        h.textContent = `${fy(yr)}, per surveyed farm`;
        if (!n) { body.innerHTML = '<p class="empty-state">No national ledger was published for this year.</p>'; return; }
        const prov = core.provisionalYear === yr;
        const idx = core.years.indexOf(yr);
        const prev = core.years[idx - 1];
        const next = core.years[idx + 1];

        body.innerHTML = `
          ${prov ? '<p class="note warn"><strong>Provisional.</strong> ABARES will revise this year. It is excluded from every total, mean and share on this page.</p>' : ''}
          <div class="table-scroll">
            <table>
              <tbody>
                ${ledgerRow('Total cash receipts', n.receipts)}
                ${ledgerRow('less total cash costs', n.costs, true)}
                ${ledgerRow('= farm cash income', n.cashIncome, false, true)}
                ${ledgerRow('less depreciation', n.depreciation, true)}
                ${ledgerRow('less imputed labour (the family\'s unpaid hours, priced)', n.imputedLabour, true)}
                ${ledgerRow('plus change in trading stock', n.stockBuildup)}
                ${ledgerRow('= farm business profit', n.businessProfit, false, true)}
                ${ledgerRow('Profit at full equity (before the cost of borrowing)', n.profitFe)}
                ${ledgerRow('plus capital appreciation', n.appreciation)}
                ${ledgerRow('= profit at full equity, counting the land', n.profitFeInc, false, true)}
              </tbody>
            </table>
          </div>
          <p class="small">Rate of return that year: <strong class="mono">${esc(signedPct(n.rateEx))}</strong> counting
            the farming only, <strong class="mono">${esc(signedPct(n.rateInc))}</strong> once the land is counted.
            Land value per farm: <strong class="mono">${esc(money(n.landValue, { long: true }))}</strong>; debt
            <span class="mono">${esc(money(n.debt, { long: true }))}</span>; the survey represents
            <span class="mono">${esc(num(n.population))}</span> broadacre farms of
            <span class="mono">${esc(num(n.area))}</span> hectares each.</p>
          ${identityNote(n)}
          <p class="note warn small"><strong>One sum that looks like it should work, and doesn't.</strong> Profit at
            full equity is not farm business profit plus interest plus land rent. That identity fails in nearly two
            in five region-years of this survey, so this site never draws it, and neither should you.</p>
          <div class="row" style="flex-wrap: wrap">
            ${prev != null ? `<button type="button" class="btn btn-sm" data-go="${prev}">◀ ${esc(fy(prev))}</button>` : ''}
            ${next != null ? `<button type="button" class="btn btn-sm" data-go="${next}">${esc(fy(next))} ▶</button>` : ''}
          </div>
          <p class="xsmall muted">${esc(SCOPE_SHORT)}</p>`;
      };

      body.addEventListener('click', (e) => {
        const t = (e.target as Element).closest('[data-go]');
        if (!t) return;
        const to = Number(t.getAttribute('data-go'));
        if (Number.isFinite(to) && to !== shown) fill(to);
      });

      fill(year);
    },
  });
}

function ledgerRow(label: string, value: number | null, minus = false, total = false): string {
  const shown = value == null ? '—' : `${minus ? '−' : ''}${money(Math.abs(value), { long: true })}`;
  const neg = value != null && value < 0;
  return `<tr>
    <td${total ? ' style="font-weight: 650"' : ''}>${esc(label)}</td>
    <td class="num${neg ? ' neg' : ''}"${total ? ' style="font-weight: 650"' : ''}>${esc(shown)}${neg && !minus ? ' <span class="xsmall">(negative)</span>' : ''}</td>
  </tr>`;
}

/** ABARES publishes these components rounded to $10, so the identities can miss by tens. */
function identityNote(n: NationalYear): string {
  const checks: Array<[string, number | null]> = [
    ['receipts − costs = farm cash income', diff(n.receipts, n.costs, n.cashIncome)],
    ['cash income − depreciation − imputed labour + stock change = business profit',
      n.cashIncome == null || n.depreciation == null || n.imputedLabour == null || n.stockBuildup == null || n.businessProfit == null
        ? null : n.cashIncome - n.depreciation - n.imputedLabour + n.stockBuildup - n.businessProfit],
    ['profit at full equity + appreciation = profit counting the land',
      n.profitFe == null || n.appreciation == null || n.profitFeInc == null ? null : n.profitFe + n.appreciation - n.profitFeInc],
  ];
  const off = checks.filter(([, d]) => d != null && Math.abs(d) > 0);
  if (!off.length) return '<p class="xsmall muted">All three published sums balance to the dollar in this year.</p>';
  return `<p class="xsmall muted">Published components are rounded to the nearest $10, so ${off.length === 1 ? 'one line is' : `${off.length} lines are`}
    out by a few tens of dollars: ${off.map(([l, d]) => `${esc(l)} (${esc(money(d, { long: true }))})`).join('; ')}. That is rounding, not a broken identity.</p>`;
}

function diff(a: number | null, b: number | null, c: number | null): number | null {
  if (a == null || b == null || c == null) return null;
  return a - b - c;
}

/* ──────────────────────────────── helpers ─────────────────────────────────── */

function swatch(colour: string, label: string): string {
  return `<span class="legend-item"><span class="legend-swatch" style="background: ${colour}"></span>${esc(label)}</span>`;
}
